import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { MapPin, Calendar, DollarSign, Save, X, Wallet, CalendarDays, Briefcase, Plus, Move, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

interface TourWidget {
  id: string;
  user_id: string;
  price_from: string | null;
  price_to: string | null;
  destination: string | null;
  date_start: string | null;
  date_end: string | null;
  comment: string | null;
  created_at: string;
}

export default function TourSearchButton() {
  const { user } = useAuth();
  const [showWidget, setShowWidget] = useState(false);
  const [widgets, setWidgets] = useState<TourWidget[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const [widgetData, setWidgetData] = useState({
    priceFrom: '',
    priceTo: '',
    destination: '',
    dateStart: '',
    dateEnd: '',
    comment: '',
  });

  useEffect(() => {
    if (user && showWidget) {
      loadWidgets();
    }
  }, [user, showWidget]);

  const loadWidgets = async () => {
    const { data } = await supabase
      .from('tour_widgets')
      .select('*')
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });
    if (data) {
      setWidgets(data);
    }
  };

  const handleWidgetChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setWidgetData({ ...widgetData, [e.target.name]: e.target.value });
  };

  const clearAll = () => {
    setWidgetData({ priceFrom: '', priceTo: '', destination: '', dateStart: '', dateEnd: '', comment: '' });
  };

  const saveWidget = async () => {
    const priceFrom = Number(widgetData.priceFrom) || 0;
    const priceTo = Number(widgetData.priceTo) || 0;

    if (priceFrom && priceTo && priceFrom >= priceTo) {
      toast.error('Цена "От" должна быть меньше цены "До"');
      return;
    }

    if (!widgetData.destination && !widgetData.priceFrom) {
      toast.error('Заполните хотя бы направление или цену');
      return;
    }

    await supabase.from('tour_widgets').insert({
      user_id: user?.id,
      price_from: widgetData.priceFrom || null,
      price_to: widgetData.priceTo || null,
      destination: widgetData.destination || null,
      date_start: widgetData.dateStart || null,
      date_end: widgetData.dateEnd || null,
      comment: widgetData.comment || null,
    });

    clearAll();
    setIsCollapsed(true);
    loadWidgets();
    toast.success('Вариант сохранен');
  };

  const deleteWidget = async (id: string) => {
    await supabase.from('tour_widgets').delete().eq('id', id);
    loadWidgets();
    toast.success('Вариант удален');
  };

  const formatNumber = (num: string | null) => {
    if (!num) return '0';
    return Number(num).toLocaleString('ru-RU');
  };

  const openInPopup = () => {
    const width = 420;
    const height = 700;
    const left = window.screenX + window.outerWidth - width - 50;
    const top = window.screenY + 50;
    
    const popupWindow = window.open(
      '/tour-widget-popup',
      'TourSearchWidget',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    
    if (popupWindow) {
      setShowWidget(false);
    }
  };

  const priceFrom = Number(widgetData.priceFrom) || 0;
  const priceTo = Number(widgetData.priceTo) || 0;
  const isPriceError = widgetData.priceFrom && widgetData.priceTo && priceFrom >= priceTo;
  const canSave = !isPriceError && (widgetData.destination || (widgetData.priceFrom && widgetData.priceTo));

  return (
    <>
      {/* Widget Button */}
      <button
        onClick={() => setShowWidget(!showWidget)}
        className="fixed bottom-24 md:bottom-8 right-4 md:right-8 w-14 h-14 bg-blue-600 text-white rounded-full shadow-2xl hover:bg-blue-700 transition-all hover:scale-110 flex items-center justify-center z-30 touch-manipulation active:scale-105"
      >
        <Briefcase size={24} />
      </button>

      {/* Widget Overlay */}
      {showWidget && (
        <div
          className="fixed md:w-96 w-full rounded-t-3xl md:rounded-3xl shadow-2xl border backdrop-blur-xl overflow-hidden z-40 flex flex-col bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-700"
          style={
            window.innerWidth >= 768
              ? { right: '2rem', bottom: '2rem', maxHeight: '75vh' }
              : { left: 0, bottom: 0, maxHeight: '80vh' }
          }
        >
          <div className="p-4 md:p-5 border-b flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <Briefcase size={18} />
              <span className="font-bold text-sm md:text-base">Быстрый подбор</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={clearAll} className="text-xs text-slate-400 hover:text-red-500 transition-colors font-medium touch-manipulation px-2 py-1">
                Очистить всё
              </button>
              <button 
                onClick={openInPopup}
                title="Открыть в отдельном окне"
                className="text-slate-400 hover:text-blue-500 transition-colors touch-manipulation p-2 min-w-[40px] min-h-[40px] flex items-center justify-center"
              >
                <ExternalLink size={18} />
              </button>
              <button 
                onClick={() => setShowWidget(false)} 
                className="text-slate-400 hover:text-slate-600 transition-colors touch-manipulation p-2 min-w-[40px] min-h-[40px] flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto custom-scroll">
            {!isCollapsed ? (
              <>
                {widgets.length > 0 && (
                  <div className="flex justify-end mb-2">
                    <button onClick={() => setIsCollapsed(true)} className="text-slate-400 hover:text-slate-600">
                      <X size={16} />
                    </button>
                  </div>
                )}

                {isPriceError && (
                  <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded-xl text-sm font-medium dark:bg-red-900/30 dark:border-red-700">
                    Ошибка: Цена "От" должна быть меньше цены "До".
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Цена от</label>
                    <input
                      type="number"
                      name="priceFrom"
                      value={widgetData.priceFrom}
                      onChange={handleWidgetChange}
                      className={`w-full rounded-xl px-3 py-2.5 md:py-2 text-sm md:text-xs font-bold focus:outline-none border min-h-[44px] md:min-h-[auto] ${
                        isPriceError
                          ? 'border-red-500'
                          : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Цена до</label>
                    <input
                      type="number"
                      name="priceTo"
                      value={widgetData.priceTo}
                      onChange={handleWidgetChange}
                      className={`w-full rounded-xl px-3 py-2.5 md:py-2 text-sm md:text-xs font-bold focus:outline-none border min-h-[44px] md:min-h-[auto] ${
                        isPriceError
                          ? 'border-red-500'
                          : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Направление</label>
                  <input
                    name="destination"
                    value={widgetData.destination}
                    onChange={handleWidgetChange}
                    className="w-full rounded-xl px-3 py-2.5 md:py-2 text-sm focus:outline-none border bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 min-h-[44px] md:min-h-[auto]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Дата вылета</label>
                    <input
                      type="date"
                      name="dateStart"
                      value={widgetData.dateStart}
                      onChange={handleWidgetChange}
                      className="w-full rounded-xl px-3 py-2.5 md:py-2 text-sm md:text-xs font-bold focus:outline-none border bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 min-h-[44px] md:min-h-[auto]"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Дата возврата</label>
                    <input
                      type="date"
                      name="dateEnd"
                      value={widgetData.dateEnd}
                      onChange={handleWidgetChange}
                      className="w-full rounded-xl px-3 py-2.5 md:py-2 text-sm md:text-xs font-bold focus:outline-none border bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 min-h-[44px] md:min-h-[auto]"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Комментарий</label>
                  <textarea
                    name="comment"
                    value={widgetData.comment}
                    onChange={handleWidgetChange}
                    className="w-full rounded-xl px-3 py-2.5 md:py-2 text-sm focus:outline-none border resize-none h-20 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                    placeholder="Пожелания..."
                  />
                </div>

                <button
                  onClick={saveWidget}
                  disabled={!canSave}
                  className={`w-full py-3 rounded-xl font-semibold shadow-lg transition-all touch-manipulation min-h-[48px] ${
                    canSave ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800' : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  Сохранить
                </button>
              </>
            ) : (
              <div className="flex justify-center">
                <button
                  onClick={() => setIsCollapsed(false)}
                  className="p-2 rounded-full bg-blue-50 hover:bg-blue-100 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-600"
                >
                  <Plus size={20} />
                </button>
              </div>
            )}

            {widgets.length > 0 && (
              <div className="mt-2 pt-4 border-t border-slate-100 dark:border-slate-700">
                <h4 className="text-xs font-bold uppercase text-slate-400 mb-3">Сохраненные варианты</h4>
                <div className="space-y-3">
                  {widgets.map(w => (
                    <div
                      key={w.id}
                      className="p-4 rounded-xl border text-sm relative group bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                    >
                      <div className="font-bold text-base mb-1 text-blue-500">{w.destination || 'Без направления'}</div>
                      <div className="flex flex-col gap-1 text-xs opacity-80">
                        {w.price_from && w.price_to && (
                          <div className="flex items-center">
                            <Wallet size={12} className="inline mr-1" />
                            <span>{formatNumber(w.price_from)}</span>
                            <span className="mx-2 text-slate-400">—</span>
                            <span>{formatNumber(w.price_to)}</span>
                          </div>
                        )}
                        {w.date_start && w.date_end && (
                          <div>
                            <CalendarDays size={12} className="inline mr-1" />
                            {w.date_start} → {w.date_end}
                          </div>
                        )}
                        {w.comment && <div className="mt-1 italic opacity-70">{w.comment}</div>}
                      </div>
                      <button
                        onClick={() => deleteWidget(w.id)}
                        className="absolute top-3 right-3 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
